import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Check, ChevronRight, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AppDrawer } from '@/components/AppDrawer';
import { SingleChips } from '@/components/ChoiceChips';
import { Field } from '@/components/Field';
import { Bar, Group, IconDisc, Row, RowText, Section, Spinner } from '@/components/kit';
import { NamedIcon } from '@/lib/icons';
import { api } from '@/lib/api';
import { formatDate, m, msg } from '@/lib/i18n';
import { useAction } from '@/lib/queries';
import type { AppState } from '@/lib/types';

type Timer = AppState['timers'][number];
type Kind = Timer['kind'];

const ICON: Record<Kind, string> = { washer: 'washing-machine', dryer: 'wind', dishwasher: 'glass-water', oven: 'cooking-pot', other: 'timer' };
const DEFAULT_MINUTES: Record<Kind, number> = { washer: 60, dryer: 90, dishwasher: 120, oven: 30, other: 15 };
const PRESETS = [15, 30, 45, 60, 90, 120, 180];
const MAX_MINUTES = 360;
const LAST_KEY = 'cr_timer_minutes';

const clock = (ms: number) => formatDate(ms, { hour: '2-digit', minute: '2-digit' });

export function durationLabel(minutes: number) {
  if (minutes < 60) return m.minutes_short({ minutes });
  const hours = Math.floor(minutes / 60);
  return minutes % 60 ? m.duration_h_m({ hours, minutes: minutes % 60 }) : m.duration_h({ hours });
}

/** Time left, as "35 min", or "4:05" in the last ten minutes. */
function leftLabel(ms: number) {
  const s = Math.max(0, Math.ceil(ms / 1000));
  if (s >= 600) return m.minutes_short({ minutes: Math.ceil(s / 60) });
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** Ticks every second while something is counting down. */
function useNow(active: boolean) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!active) return;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);
  return now;
}

/** Remembered durations per machine: a per-device convenience, so it may be missing. */
function lastMinutes(): Partial<Record<Kind, number>> {
  try {
    return JSON.parse(localStorage.getItem(LAST_KEY) ?? '{}') ?? {};
  } catch {
    return {};
  }
}
function rememberMinutes(kind: Kind, minutes: number) {
  try {
    localStorage.setItem(LAST_KEY, JSON.stringify({ ...lastMinutes(), [kind]: minutes }));
  } catch {
    /* private mode */
  }
}

/**
 * Machine timers on Today: what's running in the household, with the time it
 * ends (CREME's timer shows the clock time next to the countdown), and a row
 * to start one. Whoever started it gets a push when it's done.
 */
export function MachineTimers({ state }: { state: AppState }) {
  const [open, setOpen] = useState(false);
  const timers = state.timers;
  const now = useNow(timers.some((t) => t.endsAt > Date.now()));

  // A timer running out while the app is open: tell them here too.
  const seen = useRef<Set<number> | null>(null);
  useEffect(() => {
    const done = timers.filter((t) => t.endsAt <= now);
    if (seen.current === null) {
      seen.current = new Set(done.map((t) => t.id));
      return;
    }
    for (const t of done) {
      if (seen.current.has(t.id)) continue;
      seen.current.add(t.id);
      if (t.by?.id === state.me.id) {
        navigator.vibrate?.([200, 100, 200]);
        toast(msg(`push_timer_${t.kind}_title`), { description: msg(`push_timer_${t.kind}_body`) });
      }
    }
  }, [now, timers, state.me.id]);

  const start = (
    <Row onClick={() => setOpen(true)}>
      <IconDisc>
        <NamedIcon name="washing-machine" />
      </IconDisc>
      <RowText title={m.timer_new()} sub={m.timer_new_sub()} />
      <ChevronRight className="text-muted-foreground size-[18px]" />
    </Row>
  );

  return (
    <>
      {timers.length ? (
        <Section title={m.timers_title()}>
          <Group>
            {timers.map((t) => (
              <TimerRow key={t.id} timer={t} now={now} mine={t.by?.id === state.me.id} />
            ))}
            {start}
          </Group>
        </Section>
      ) : (
        <Group>{start}</Group>
      )}
      <StartTimerDrawer state={state} open={open} onOpenChange={setOpen} />
    </>
  );
}

function TimerRow({ timer: t, now, mine }: { timer: Timer; now: number; mine: boolean }) {
  const stop = useAction(() => api('DELETE', `/timers/${t.id}`));
  const done = t.endsAt <= now;
  const total = t.endsAt - t.startedAt;
  const who = !mine && t.by ? ` · ${t.by.name}` : '';
  return (
    <div className="flex min-h-14 items-center gap-3 px-4 py-3">
      <IconDisc tone={done ? 'accent' : 'muted'}>
        <NamedIcon name={ICON[t.kind]} />
      </IconDisc>
      <div className="grid min-w-0 flex-1 gap-1.5">
        <div className="flex items-baseline justify-between gap-2">
          <span className="truncate font-medium">{msg(`timer_kind_${t.kind}`)}</span>
          {!done && <span className="shrink-0 font-semibold tabular-nums">{leftLabel(t.endsAt - now)}</span>}
        </div>
        {done ? (
          <span className="text-muted-foreground text-[13px]">
            {m.timer_finished_at({ time: clock(t.endsAt) })}
            {who}
          </span>
        ) : (
          <>
            <Bar value={total > 0 ? 1 - (t.endsAt - now) / total : 1} />
            <span className="text-muted-foreground text-[13px]">
              {m.timer_done_at({ time: clock(t.endsAt) })}
              {who}
            </span>
          </>
        )}
      </div>
      {done ? (
        <Button variant="secondary" size="sm" onClick={() => stop.mutate()} disabled={stop.isPending}>
          {stop.isPending ? <Spinner /> : <Check />}
          {m.timer_clear()}
        </Button>
      ) : (
        <Button variant="ghost" size="icon" className="text-muted-foreground shrink-0" onClick={() => stop.mutate()} disabled={stop.isPending} aria-label={m.timer_stop()}>
          {stop.isPending ? <Spinner /> : <X />}
        </Button>
      )}
    </div>
  );
}

function StartTimerDrawer({ state, open, onOpenChange }: { state: AppState; open: boolean; onOpenChange: (o: boolean) => void }) {
  const home = state.household.home;
  // Only the machines this home has (when we know).
  const kinds = (['washer', 'dryer', 'dishwasher', 'oven', 'other'] as Kind[]).filter(
    (k) => !home || (k !== 'dryer' && k !== 'dishwasher') || home.things.includes(k),
  );
  const [kind, setKind] = useState<Kind>('washer');
  const [choice, setChoice] = useState(String(DEFAULT_MINUTES.washer));
  const [custom, setCustom] = useState('');
  const [error, setError] = useState<string | null>(null);
  const now = useNow(open);

  const pickKind = (k: Kind) => {
    setKind(k);
    const minutes = lastMinutes()[k] ?? DEFAULT_MINUTES[k];
    if (PRESETS.includes(minutes)) setChoice(String(minutes));
    else {
      setChoice('custom');
      setCustom(String(minutes));
    }
    setError(null);
  };
  useEffect(() => {
    if (open) pickKind('washer');
  }, [open]);

  const minutes = choice === 'custom' ? Number(custom) : Number(choice);
  const valid = Number.isInteger(minutes) && minutes >= 1 && minutes <= MAX_MINUTES;
  const startTimer = useAction((body: { kind: Kind; minutes: number }) => api('POST', '/timers', body), {
    onSuccess: () => {
      rememberMinutes(kind, minutes);
      onOpenChange(false);
    },
  });
  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    if (!valid) return setError(m.err_timer_minutes());
    startTimer.mutate({ kind, minutes });
  };

  return (
    <AppDrawer
      open={open}
      onOpenChange={(o) => !startTimer.isPending && onOpenChange(o)}
      title={m.timer_new()}
      description={state.me.pushSubscriptions ? m.timer_intro() : m.timer_intro_no_push()}
      footer={
        <Button size="lg" onClick={() => submit()} disabled={startTimer.isPending}>
          {startTimer.isPending && <Spinner />}
          {valid ? m.timer_start_at({ time: clock(now + minutes * 60_000) }) : m.timer_start()}
        </Button>
      }
    >
      <form noValidate onSubmit={submit} className="grid gap-5">
        <SingleChips label={m.timer_machine()} options={kinds.map((k) => ({ value: k, label: msg(`timer_kind_${k}`), icon: ICON[k] }))} value={kind} onChange={pickKind} />
        <div className="grid gap-2">
          <span className="text-sm font-semibold">{m.timer_how_long()}</span>
          <SingleChips
            label={m.timer_how_long()}
            options={[...PRESETS.map((p) => ({ value: String(p), label: durationLabel(p) })), { value: 'custom', label: m.freq_custom() }]}
            value={choice}
            onChange={(v) => {
              setChoice(v);
              setError(null);
            }}
          />
        </div>
        {choice === 'custom' && (
          <Field id="timerMinutes" label={m.timer_minutes()} error={error}>
            <Input
              id="timerMinutes"
              type="number"
              inputMode="numeric"
              min={1}
              max={MAX_MINUTES}
              value={custom}
              onChange={(e) => {
                setCustom(e.target.value);
                setError(null);
              }}
              aria-invalid={!!error}
              aria-describedby={error ? 'timerMinutes-error' : undefined}
              className="h-12 rounded-xl"
            />
          </Field>
        )}
      </form>
    </AppDrawer>
  );
}
